<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { BoxComponent, ButtonComponent, FlexComponent, ImageComponent, ThumbnailOnlyBadge } from '..'
import ArrowIcon from '../../assets/icons/arrow.svg?component'
import PlayIcon from '../../assets/icons/play.svg?component'
import type { Asset } from '../../types/common.types'
import { isThumbnailOnlyVideo, resolveVideoPreload } from '../../utils'

export type CarouselItem = {
  id: string | number
  asset: Asset
  label: string
}

export type CarouselSliderProps = {
  items: CarouselItem[]
  activeItemId?: string | number
  infinite?: boolean
  width?: number
  height?: number
}

const props = withDefaults(defineProps<CarouselSliderProps>(), {
  infinite: false,
  width: 360,
  height: 640,
})

const widthPx = computed(() => `${props.width}px`)
const heightPx = computed(() => `${props.height}px`)
const itemOffsetPx = computed(() => `${props.width + 10}px`)
const transitionOffsetPx = computed(() => `${props.width + 100}px`)
const emit = defineEmits<(e: 'change', item: CarouselItem) => void>()

const getIndexById = (id: string | number | undefined) => {
  if (!id) {
    return 0
  }
  const index = props.items.findIndex((item) => item.id === id)
  return index >= 0 ? index : 0
}

const currentIndex = ref(getIndexById(props.activeItemId))
const isTransitioning = ref(false)

watch(
  () => props.activeItemId,
  (newId) => {
    currentIndex.value = getIndexById(newId)
  },
)
const currentPlayingId = ref<string | number | null>(null)
const currentVideoElement = ref<HTMLVideoElement | null>(null)
const slideDirection = ref<'left' | 'right'>('right')

const getIndex = (offset: number) => {
  const totalItems = props.items.length
  if (props.infinite) {
    return (currentIndex.value + offset + totalItems) % totalItems
  }
  const index = currentIndex.value + offset
  return index >= 0 && index < totalItems ? index : -1
}

const visibleItems = computed(() => {
  const items: {
    item: CarouselItem
    position: 'left' | 'center' | 'right'
    index: number
  }[] = []
  const totalItems = props.items.length

  if (totalItems === 0) {
    return items
  }

  const positions: { offset: number; position: 'left' | 'center' | 'right' }[] = [
    { offset: -1, position: 'left' },
    { offset: 0, position: 'center' },
    { offset: 1, position: 'right' },
  ]

  positions.forEach(({ offset, position }) => {
    const index = getIndex(offset)
    if (index !== -1) {
      items.push({
        item: props.items[index]!,
        position,
        index,
      })
    }
  })

  return items
})

const canNavigate = (direction: 'left' | 'right') => {
  if (props.items.length <= 1) {
    return false
  }
  if (props.infinite) {
    return true
  }
  return direction === 'left' ? currentIndex.value > 0 : currentIndex.value < props.items.length - 1
}

const navigate = (direction: 'left' | 'right') => {
  if (!canNavigate(direction) || isTransitioning.value) {
    return
  }

  if (currentVideoElement.value && currentPlayingId.value !== null) {
    currentVideoElement.value.pause()
    currentVideoElement.value = null
    currentPlayingId.value = null
  }

  isTransitioning.value = true
  slideDirection.value = direction

  const offset = direction === 'left' ? -1 : 1
  const newIndex = getIndex(offset)

  if (newIndex !== -1) {
    currentIndex.value = newIndex
    emit('change', props.items[currentIndex.value]!)
  }

  setTimeout(() => {
    isTransitioning.value = false
  }, 600)
}

const toggleVideoPlay = (item: CarouselItem, videoElement: HTMLVideoElement) => {
  const isPlaying = currentPlayingId.value === item.id

  if (!isPlaying && currentVideoElement.value && currentPlayingId.value !== null) {
    currentVideoElement.value.pause()
  }

  if (isPlaying) {
    videoElement.pause()
    currentPlayingId.value = null
    currentVideoElement.value = null
    return
  }

  void videoElement.play()
  currentPlayingId.value = item.id
  currentVideoElement.value = videoElement
}

const isVideoPlaying = (item: CarouselItem) => currentPlayingId.value === item.id
const isThumbnailOnly = (item: CarouselItem) => isThumbnailOnlyVideo(item.asset)
const handlePlayButtonClick = (event: MouseEvent, item: CarouselItem) => {
  const video = (event.currentTarget as HTMLElement).parentElement?.querySelector('video')
  if (video) {
    toggleVideoPlay(item, video)
  }
}
</script>

<template>
  <BoxComponent class="carousel-slider" w="100%" h="100%" :overflow-hidden="true">
    <FlexComponent class="carousel-slider__container" align="center" justify="center">
      <TransitionGroup :name="`slide-${slideDirection}`">
        <BoxComponent
          v-for="{ item, position, index } in visibleItems"
          :key="`${item.id}-${index}`"
          class="carousel-slider__item"
          :class="`carousel-slider__item--${position}`"
        >
          <BoxComponent w="100%" :overflow-hidden="true">
            <ImageComponent
              v-if="item.asset.type === 'image' && (item.asset.key || item.asset.url)"
              :image="item.asset"
              :width="width"
              :height="height"
              :alt="item.label"
              class="carousel-slider__image"
            />
            <template v-else-if="isThumbnailOnly(item)">
              <img v-if="item.asset.thumbnailUrl" :src="item.asset.thumbnailUrl" :alt="item.label" class="carousel-slider__image" />
              <ThumbnailOnlyBadge class="carousel-slider__thumbnail-only-badge" />
            </template>
            <template v-else-if="item.asset.type === 'video'">
              <video
                :src="item.asset.url"
                :poster="item.asset.thumbnailUrl"
                class="carousel-slider__video"
                :class="{ 'carousel-slider__video--playing': isVideoPlaying(item) }"
                :controls="isVideoPlaying(item)"
                :preload="resolveVideoPreload(item.asset)"
                @click.stop
              />
              <ButtonComponent
                v-if="!isVideoPlaying(item)"
                class="carousel-slider__play-button"
                variant="transparent"
                is-icon
                @click.stop="(e: MouseEvent) => handlePlayButtonClick(e, item)"
              >
                <PlayIcon />
              </ButtonComponent>
            </template>
          </BoxComponent>
        </BoxComponent>
      </TransitionGroup>
    </FlexComponent>
    <ButtonComponent
      v-if="canNavigate('left')"
      class="carousel-slider__nav carousel-slider__nav--left"
      variant="outlined"
      is-icon
      @click="navigate('left')"
    >
      <ArrowIcon />
    </ButtonComponent>
    <ButtonComponent
      v-if="canNavigate('right')"
      class="carousel-slider__nav carousel-slider__nav--right"
      variant="outlined"
      is-icon
      @click="navigate('right')"
    >
      <ArrowIcon style="transform: rotate(180deg)" />
    </ButtonComponent>
  </BoxComponent>
</template>
<style scoped lang="scss">
.carousel-slider {
  position: relative;

  &__container {
    position: relative;
    width: 100%;
    height: 100%;
  }

  &__item {
    position: absolute;
    transition: transform 0.6s cubic-bezier(0.25, 0.1, 0.25, 1);
    backface-visibility: hidden;
    transform-origin: center center;

    &--left {
      transform: translate3d(calc(v-bind(itemOffsetPx) * -1), 0, 0) scale(0.8);
    }

    &--center {
      transform: translate3d(0, 0, 0) scale(1);
    }

    &--right {
      transform: translate3d(v-bind(itemOffsetPx), 0, 0) scale(0.8);
    }
  }

  &__image {
    display: block;
    width: v-bind(widthPx);
    height: v-bind(heightPx);
    object-fit: cover;
    border-radius: 16px;
  }

  &__video {
    display: block;
    width: v-bind(widthPx);
    height: v-bind(heightPx);
    object-fit: cover;
    border-radius: 16px;
    cursor: pointer;

    &:fullscreen,
    &:-webkit-full-screen {
      object-fit: contain;
    }
  }

  &__video--playing {
    cursor: default;
  }

  &__play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    backdrop-filter: blur(4px);
    z-index: 2;
  }

  &__thumbnail-only-badge {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }

  &__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);

    &--left {
      left: 8px;
    }

    &--right {
      right: 8px;
    }
  }
}

.slide-right-enter-from,
.slide-left-leave-to {
  transform: translate3d(v-bind(transitionOffsetPx), 0, 0) scale(0.8);
}

.slide-right-leave-to,
.slide-left-enter-from {
  transform: translate3d(calc(v-bind(transitionOffsetPx) * -1), 0, 0) scale(0.8);
}
</style>
